import { t } from "i18next";

import {
  AppBar,
  Box,
  Button,
  Stack,
  type SxProps,
  type Theme,
  Toolbar,
  Typography,
  useTheme
} from "@mui/material";

import { isDarkMode, onPrimarySurfaceColor } from "@/src/theme/helpers";

import BaanaLogo from "@/src/components/atoms/BaanaLogo/BaanaLogo";

interface RouteErrorViewProps {
  title: string;
  message: string;
  showRetry?: boolean;
  showBackToBlog?: boolean;
}

const handleRetry = () => {
  window.location.reload();
};

const pageSx: SxProps<Theme> = (muiTheme) => ({
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  bgcolor: muiTheme.palette.background.default
});

const headerToolbarSx: SxProps<Theme> = {
  justifyContent: "flex-start"
};

const titleSx: SxProps<Theme> = (muiTheme) => ({
  fontSize: [28, 36],
  color: muiTheme.palette.text.primary
});

const messageSx: SxProps<Theme> = (muiTheme) => ({
  color: isDarkMode(muiTheme)
    ? muiTheme.palette.text.primary
    : muiTheme.palette.text.secondary
});

// Branded, on-brand fallback for the root ErrorBoundary. Intentionally avoids
// the real Header/Footer (which depend on auth/router/data that may be the
// source of the crash) and uses anchors instead of <Link> so navigation works
// even when the router is in a broken state.
const RouteErrorView = ({
  title,
  message,
  showRetry = true,
  showBackToBlog = false
}: RouteErrorViewProps) => {
  const muiTheme = useTheme();

  return (
    <Box sx={pageSx}>
      <AppBar position="relative">
        <Toolbar sx={headerToolbarSx}>
          <Box
            component="a"
            href="/"
            aria-label="Baana"
            sx={{ display: "inline-block", width: 110 }}
          >
            <BaanaLogo color={onPrimarySurfaceColor(muiTheme)} />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 6
        }}
      >
        <Stack
          spacing={3}
          sx={{ alignItems: "center", maxWidth: 480, textAlign: "center" }}
        >
          <Typography variant="h1" sx={titleSx}>
            {title}
          </Typography>
          <Typography variant="body1" sx={messageSx}>
            {message}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              gap: 2
            }}
          >
            {showRetry && (
              <Button variant="contained" color="primary" onClick={handleRetry}>
                {t("general.retry")}
              </Button>
            )}
            {showBackToBlog && (
              <Button
                variant="outlined"
                color="primary"
                component="a"
                href="/blog"
              >
                {t("general.backToBlog")}
              </Button>
            )}
            <Button
              variant={showBackToBlog ? "text" : "outlined"}
              color="primary"
              component="a"
              href="/"
            >
              {t("general.backToHome")}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default RouteErrorView;
